<script setup lang="ts">
import { computed, ref, type CSSProperties } from 'vue';
import { useSidebarLayout } from './sidebarLayout';
import './styles.scss';

const props = withDefaults(
  defineProps<{
    variant?: 'page' | 'app';
    scrollY?: boolean;
    defaultWidth?: number;
    collapsedWidth?: number;
    toggleOnResizeTolerance?: number;
    contentClass?: string;
    showToggle?: boolean;
  }>(),
  {
    variant: 'page',
    scrollY: true,
    defaultWidth: 200,
    collapsedWidth: 30,
    toggleOnResizeTolerance: 50,
    contentClass: 'px-3 py-4',
    showToggle: true,
  },
);

const emit = defineEmits<{
  'sidebar:toggle': [collapsed: boolean];
}>();

const isAppVariant = props.variant === 'app';

// App variant
const appLayout = isAppVariant ? useSidebarLayout() : null;

// Page variant
const pageCollapsed = ref(false);
const pageWidth = ref(props.defaultWidth);
const pagePreviousWidth = ref(props.defaultWidth);

function pageToggle() {
  if (pageCollapsed.value) {
    pageWidth.value = pagePreviousWidth.value;
  } else {
    pagePreviousWidth.value = pageWidth.value;
    pageWidth.value = props.collapsedWidth;
  }
  pageCollapsed.value = !pageCollapsed.value;
}

function pageOnPointerDown(e: PointerEvent) {
  const startX = e.clientX;
  const startW = pageWidth.value;
  const threshold = props.collapsedWidth + props.toggleOnResizeTolerance;

  const onMove = (ev: PointerEvent) => {
    const current = Math.max(
      props.collapsedWidth,
      startW + (ev.clientX - startX),
    );
    pageWidth.value = Math.max(props.collapsedWidth, Math.round(current));

    if (pageCollapsed.value && current > threshold) {
      pageCollapsed.value = false;
    } else if (!pageCollapsed.value && current < threshold) {
      pageCollapsed.value = true;
    }
  };

  const onUp = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp, true);

    if (pageWidth.value < threshold) {
      pageWidth.value = props.collapsedWidth;
      pageCollapsed.value = true;
    } else {
      pageCollapsed.value = false;
    }
  };

  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp, true);
}

// Shares the same logic
const collapsed = computed(() =>
  isAppVariant ? appLayout!.collapsed.value : pageCollapsed.value,
);

function toggle() {
  if (isAppVariant) {
    appLayout!.toggle();
  } else {
    pageToggle();
    emit('sidebar:toggle', pageCollapsed.value);
  }
}

function onPointerDown(e: PointerEvent) {
  if (isAppVariant) {
    appLayout!.onPointerDown(e);
  } else {
    pageOnPointerDown(e);
  }
}

const iconClass = computed(() =>
  collapsed.value ? 'fas fa-angle-double-right' : 'fas fa-angle-double-left',
);

const sidebarClass = computed(() =>
  collapsed.value ? 'collapsed' : 'expanded',
);

const asideStyle = computed<CSSProperties>(() => {
  if (isAppVariant) {
    const layout = appLayout!;
    const w = layout.collapsed.value
      ? layout.collapsedRailWidth.value
      : layout.width.value;
    return {
      position: 'fixed',
      top: 'var(--itp-nav-h,56px)',
      left: '0',
      bottom: '0',
      height: 'auto',
      width: `${w}px`,
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bs-body-bg)',
      borderRight: '1px solid var(--bs-border-color)',
      zIndex: 1029,
      transition: 'width var(--itp-sidebar-transition, 0.15s ease)',
    };
  }
  return {
    position: 'relative',
    width: `${pageWidth.value}px`,
    minWidth: `${props.collapsedWidth}px`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    transition: 'none',
  };
});

const contentStyle = computed<CSSProperties>(() => {
  if (isAppVariant) {
    return {
      flex: '1 1 auto',
      overflowY: props.scrollY ? 'auto' : 'visible',
      overflowX: 'hidden',
    };
  }
  if (pageCollapsed.value) {
    return {
      visibility: 'hidden',
      overflow: 'hidden',
      width: '0px',
      flex: '1 1 auto',
    };
  }
  return {
    flex: '1 1 auto',
    overflowY: props.scrollY ? 'auto' : 'visible',
    overflowX: 'hidden',
  };
});
</script>

<template>
  <aside class="itp-sidebar" :class="sidebarClass" :style="asideStyle">
    <div
      class="itp-sidebar__content"
      :class="props.contentClass"
      :style="contentStyle"
    >
      <slot name="header" />
      <slot />
      <slot name="footer" />
    </div>

    <button
      v-if="props.showToggle"
      class="itp-sidebar__toggle"
      type="button"
      @click="toggle"
    >
      <slot name="toggle-icon">
        <i :class="iconClass" />
      </slot>
    </button>

    <div
      v-if="!collapsed"
      class="itp-sidebar__resize-handle"
      role="separator"
      @pointerdown="onPointerDown"
    />
  </aside>
</template>
